import React from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowLeft } from 'lucide-react';
import { useTranslation } from '../i18n';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useCompare } from '../hooks/useCompare';
import SEOHead from '../components/SEOHead';

// Generate SEO-friendly slug from vehicle data (brand-model-year)
const generateVehicleSlug = (v) => {
  if (!v) return '';
  const brand = (v.brand || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const model = (v.model || '').toLowerCase().replace(/[^a-z0-9]/g, '').replace(new RegExp(`^${brand}`, 'i'), '');
  const year = v.year || '';
  return `${brand}-${model}-${year}`;
};

const ComparePage = ({ lang }) => {
  const { t } = useTranslation(lang);
  const { list: compareList, remove, clear } = useCompare();

  const removeFromCompare = (vehicleId) => {
    remove(vehicleId);
    toast.success(t('compare.removedFromComparison'));
  };

  const clearAll = () => {
    clear();
    toast.success(t('compare.comparisonCleared'));
  };

  if (compareList.length === 0) {
    return (
      <>
        <SEOHead
          title="Compare Vehicles | AAA of Miami"
          description="Compare vehicles side by side at AAA of Miami"
        />
        <div className="pt-20 min-h-screen flex flex-col items-center justify-center" style={{ background: 'var(--bg-secondary)' }}>
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
              {t('common.noVehicles')}
            </h1>
            <p className="mb-8" style={{ color: 'var(--text-secondary)' }}>{t('common.addVehicles')}</p>
            <Link to="/inventory">
              <Button data-testid="browse-inventory-btn">{t('common.browseInventory')}</Button>
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title="Compare Vehicles | AAA of Miami"
        description="Compare vehicles side by side at AAA of Miami"
      />
      <div className="pt-20 min-h-screen" style={{ background: 'var(--bg-secondary)' }}>
        <div className="max-w-7xl mx-auto px-4 py-12">
          <Link to="/inventory" className="inline-flex items-center mb-6" style={{ color: 'var(--text-secondary)' }} data-testid="back-to-inventory">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.backToInventory')}
          </Link>

          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }} data-testid="compare-title">
                {t('common.compareTitle')}
              </h1>
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>{t('common.compareSub')}</p>
            </div>
            <Button onClick={clearAll} variant="outline" data-testid="clear-all-btn">
              {t('common.clearAll')}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="compare-grid">
            {compareList.map(vehicle => (
              <div key={vehicle.id} className="rounded-lg shadow-lg overflow-hidden" style={{ background: 'var(--bg-primary)' }} data-testid={`compare-vehicle-${vehicle.id}`}>
                <div className="relative">
                  <img
                    src={vehicle.images?.[0] || 'https://via.placeholder.com/400x300?text=No+Image'}
                    alt={`${vehicle.brand} ${vehicle.model}`}
                    className="w-full h-48 object-cover"
                  />
                  <button
                    onClick={() => removeFromCompare(vehicle.id)}
                    className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-gray-100"
                    data-testid={`remove-vehicle-${vehicle.id}`}
                  >
                    <X className="w-4 h-4 text-gray-900" />
                  </button>
                </div>

                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
                    {vehicle.brand} {vehicle.model}
                  </h3>

                  <div className="space-y-3">
                    <div className="flex justify-between border-b pb-2" style={{ borderColor: 'var(--border-color)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{t('vehicle.price')}</span>
                      <span className="font-semibold gold-accent">${vehicle.price.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2" style={{ borderColor: 'var(--border-color)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{t('vehicle.year')}</span>
                      <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{vehicle.year}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2" style={{ borderColor: 'var(--border-color)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{t('vehicle.mileage')}</span>
                      <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{vehicle.mileage.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2" style={{ borderColor: 'var(--border-color)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{t('vehicle.transmission')}</span>
                      <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{vehicle.transmission || '—'}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2" style={{ borderColor: 'var(--border-color)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{t('vehicle.fuelType')}</span>
                      <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{vehicle.fuel_type || '—'}</span>
                    </div>
                    {vehicle.engine ? (
                      <div className="flex justify-between border-b pb-2" style={{ borderColor: 'var(--border-color)' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>{t('vehicle.engine')}</span>
                        <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{vehicle.engine}</span>
                      </div>
                    ) : null}
                    {vehicle.color ? (
                      <div className="flex justify-between border-b pb-2" style={{ borderColor: 'var(--border-color)' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>{t('vehicle.color')}</span>
                        <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{vehicle.color}</span>
                      </div>
                    ) : null}
                  </div>

                  <Link to={`/${generateVehicleSlug(vehicle)}`}>
                    <Button className="w-full mt-6" data-testid={`view-details-${vehicle.id}`}>{t('vehicle.viewDetails')}</Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ComparePage;
